import { Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { NgIf } from '@angular/common'
import { BiddingComponent } from '../bidding/bidding.component'
import { ContractDeclarationComponent } from '../contract-declaration/contract-declaration.component'
import { AppComponentService } from 'shared-components'
import { DefenderDeclarationComponent } from '../defender-declaration/defender-declaration.component'
import { SettlingComponent } from '../settling/settling.component'
import { DealingCardsComponent } from '../dealing-cards/dealing-cards.component'
import { PlayingGameComponent } from '../playing-game/playing-game.component'
import { CompletedGameComponent } from '../completed-game/completed-game.component'
import {
  GamePlay,
  GamePlayPollingService,
  GamePlayService,
  playerScoreCardNull,
  GamePlayStatus,
  GamePlayPlayer,
  gamePlayPlayerNull,
} from 'game-play-services'
import { Subscription } from 'rxjs'
import { User } from 'identity-services'
import {
  CommandErrorCode,
  CommandResponse,
  successfulCommandResponse,
  unsuccessfulCommandResponse,
  WindowService,
} from 'shared-services'
import { gameSetupSiteBaseUrlToken } from '../../providers/game-setup-site-base-url.provider'
import {
  BiddingGame,
  biddingGameNull,
  BiddingService,
  ContractDeclarationGame,
  contractDeclarationGameNull,
  ContractDeclarationService,
  DefenderDeclarationGame,
  defenderDeclarationGameNull,
  DefenderDeclarationService,
  GamePlayGameId,
  GameStatus,
  PlayingGame,
  playingGameNull,
  PlayingGameService,
  SettlingGame,
  settlingGameNull,
  SettlingService,
} from 'game-services'
import { GamePlayResultsComponent } from '../game-play-results/game-play-results.component'
import { PlayToolbarComponent } from '../play-toolbar/play-toolbar.component'

@Component({
  standalone: true,
  selector: 'sans-root',
  templateUrl: './play-app.component.html',
  styleUrls: ['./play-app.component.scss'],
  imports: [
    BiddingComponent,
    NgIf,
    ContractDeclarationComponent,
    SettlingComponent,
    DefenderDeclarationComponent,
    PlayingGameComponent,
    DealingCardsComponent,
    CompletedGameComponent,
    DealingCardsComponent,
    GamePlayResultsComponent,
    PlayToolbarComponent,
  ],
})
export class PlayAppComponent implements OnInit, OnDestroy {
  private userSubscription = new Subscription()
  public gameStatus: GameStatus = 'DealingCards'
  public shouldShowCompletedGame = false
  public gamePlayStatus: GamePlayStatus = 'Created'
  public isGamePlayFetchedAtLeastOnce = false

  public gamePlayId = ''
  public user: GamePlayPlayer = gamePlayPlayerNull
  public rightOpponent: GamePlayPlayer = gamePlayPlayerNull
  public leftOpponent: GamePlayPlayer = gamePlayPlayerNull

  public biddingGame: BiddingGame = biddingGameNull

  public contractDeclarationGame: ContractDeclarationGame =
    contractDeclarationGameNull

  public defenderDeclarationGame: DefenderDeclarationGame =
    defenderDeclarationGameNull

  public playingGame: PlayingGame = playingGameNull

  public settlingGame: SettlingGame = settlingGameNull

  public userPlayerScoreCard = playerScoreCardNull
  public leftOpponentPlayerScoreCard = playerScoreCardNull
  public rightOpponentPlayerScoreCard = playerScoreCardNull

  public constructor(
    private readonly windowService: WindowService,
    @Inject(gameSetupSiteBaseUrlToken) private gameSetupSiteBaseUrl: string,
    private readonly gamePlayPollingService: GamePlayPollingService,
    private readonly appComponentService: AppComponentService,
    private readonly biddingService: BiddingService,
    private readonly gamePlayService: GamePlayService,
    private readonly contractDeclarationService: ContractDeclarationService,
    private readonly defenderDeclarationService: DefenderDeclarationService,
    private readonly settlingService: SettlingService,
    private readonly playingGameService: PlayingGameService,
  ) {}

  public async ngOnInit(): Promise<void> {
    await this.appComponentService.onInit((user) => this.onUserFetched(user))
  }

  public ngOnDestroy(): void {
    this.userSubscription.unsubscribe()
    this.gamePlayPollingService.stopPollingForHasGamePlay()
  }

  public async onDealingCardsTimerExpired(): Promise<void> {
    await this.fetchGamePlayAndUpdate()
  }

  public async onCompletedGameTimerExpired(): Promise<void> {
    await this.fetchGamePlayAndUpdate()
  }

  public async onGameUpdated(): Promise<void> {
    await this.fetchGamePlayAndUpdate()
  }

  private async fetchGamePlayAndUpdate(): Promise<void> {
    const gamePlay = await this.gamePlayService.findGamePlay()
    await this.onGamePlayFetched(gamePlay)
  }

  private async onUserFetched(user: User): Promise<void> {
    if (!user.isSignedIn) {
      this.windowService.replace(this.gameSetupSiteBaseUrl)
      return
    }

    const gamePlay = await this.gamePlayService.findGamePlay()
    await this.onGamePlayFetched(gamePlay)
  }

  private async onGamePlayFetched(
    gamePlay: GamePlay | undefined,
  ): Promise<void> {
    if (!gamePlay) {
      this.windowService.replace(this.gameSetupSiteBaseUrl)
      return
    }

    const setGameResponse = await this.setGame(gamePlay)

    if (!setGameResponse.isSuccessful) {
      return
    }

    if (this.getShouldStartPolling(gamePlay)) {
      await this.gamePlayPollingService.startPollingForGamePlay(
        async (gamePlay) => {
          await this.onGamePlayFetched(gamePlay)
        },
      )
    }

    if (this.getShouldStopPolling(gamePlay)) {
      this.gamePlayPollingService.stopPollingForHasGamePlay()
    }

    const playerScoreCards = await this.gamePlayService.getPlayerScoreCards(
      gamePlay.userIds,
      gamePlay.gamePlayId,
    )

    this.userPlayerScoreCard =
      playerScoreCards.find(
        (scoreCard) => scoreCard.userId === gamePlay.user.userId,
      ) ?? playerScoreCardNull

    this.rightOpponentPlayerScoreCard =
      playerScoreCards.find(
        (scoreCard) => scoreCard.userId === gamePlay.rightOpponent.userId,
      ) ?? playerScoreCardNull

    this.leftOpponentPlayerScoreCard =
      playerScoreCards.find(
        (scoreCard) => scoreCard.userId === gamePlay.leftOpponent.userId,
      ) ?? playerScoreCardNull

    this.gamePlayId = gamePlay.gamePlayId
    this.gameStatus = gamePlay.currentGame.gameStatus
    this.shouldShowCompletedGame = gamePlay.shouldShowCompletedGame
    this.gamePlayStatus = gamePlay.status
    this.user = gamePlay.user
    this.rightOpponent = gamePlay.rightOpponent
    this.leftOpponent = gamePlay.leftOpponent
    this.isGamePlayFetchedAtLeastOnce = true
  }

  private getShouldStartPolling(gamePlay: GamePlay): boolean {
    if (this.gamePlayPollingService.getIsPolling()) {
      return false
    }

    return this.getShouldPoll(gamePlay)
  }

  private getShouldStopPolling(gamePlay: GamePlay): boolean {
    return !this.getShouldPoll(gamePlay)
  }

  private getShouldPoll(gamePlay: GamePlay): boolean {
    if (gamePlay.currentGame.gameStatus === 'Bidding') {
      return this.biddingGame.shouldPoll
    }

    if (gamePlay.currentGame.gameStatus === 'ContractDeclaration') {
      return this.contractDeclarationGame.shouldPoll
    }

    if (gamePlay.currentGame.gameStatus === 'DefenderDeclaration') {
      return this.defenderDeclarationGame.shouldPoll
    }

    if (gamePlay.currentGame.gameStatus === 'Playing') {
      return this.playingGame.shouldPoll
    }

    if (gamePlay.currentGame.gameStatus === 'Settling') {
      return this.settlingGame.shouldPoll
    }

    return gamePlay.status !== 'Completed'
  }

  private async setGame(
    gamePlay: GamePlay,
  ): Promise<CommandResponse<CommandErrorCode>> {
    if (gamePlay.currentGame.gameStatus === 'DealingCards') {
      return successfulCommandResponse
    }

    if (gamePlay.currentGame.gameStatus === 'Bidding') {
      return this.setBiddingGame(gamePlay.currentGame.gamePlayGameId)
    }

    if (gamePlay.currentGame.gameStatus === 'ContractDeclaration') {
      return this.setContractDeclarationGame(
        gamePlay.currentGame.gamePlayGameId,
      )
    }

    if (gamePlay.currentGame.gameStatus === 'DefenderDeclaration') {
      return this.setDefenderDeclarationGame(
        gamePlay.currentGame.gamePlayGameId,
      )
    }

    if (gamePlay.currentGame.gameStatus === 'Playing') {
      return this.setPlayingGame(gamePlay.currentGame.gamePlayGameId)
    }

    if (gamePlay.currentGame.gameStatus === 'Settling') {
      return this.setSettlingGame(gamePlay.currentGame.gamePlayGameId)
    }

    if (gamePlay.currentGame.gameStatus === 'Completed') {
      return successfulCommandResponse
    }

    return unsuccessfulCommandResponse
  }

  private async setBiddingGame(
    gamePlayGameId: GamePlayGameId,
  ): Promise<CommandResponse<CommandErrorCode>> {
    const biddingGameResponse =
      await this.biddingService.getBiddingGame(gamePlayGameId)

    if (!biddingGameResponse.isSuccessful) {
      return unsuccessfulCommandResponse
    }

    this.biddingGame = biddingGameResponse.result
    return successfulCommandResponse
  }

  private async setContractDeclarationGame(
    gamePlayGameId: GamePlayGameId,
  ): Promise<CommandResponse<CommandErrorCode>> {
    const contractDeclarationGameResponse =
      await this.contractDeclarationService.getContractDeclarationGame(
        gamePlayGameId,
      )

    if (!contractDeclarationGameResponse.isSuccessful) {
      return unsuccessfulCommandResponse
    }

    this.contractDeclarationGame = contractDeclarationGameResponse.result
    return successfulCommandResponse
  }

  private async setDefenderDeclarationGame(
    gamePlayGameId: GamePlayGameId,
  ): Promise<CommandResponse<CommandErrorCode>> {
    const defenderDeclarationGameResponse =
      await this.defenderDeclarationService.getDefenderDeclarationGame(
        gamePlayGameId,
      )

    if (!defenderDeclarationGameResponse.isSuccessful) {
      return unsuccessfulCommandResponse
    }

    this.defenderDeclarationGame = defenderDeclarationGameResponse.result
    return successfulCommandResponse
  }

  private async setPlayingGame(
    gamePlayGameId: GamePlayGameId,
  ): Promise<CommandResponse<CommandErrorCode>> {
    const playingGameResponse =
      await this.playingGameService.getPlayingGame(gamePlayGameId)

    if (!playingGameResponse.isSuccessful) {
      return unsuccessfulCommandResponse
    }

    this.playingGame = playingGameResponse.result
    return successfulCommandResponse
  }

  private async setSettlingGame(
    gamePlayGameId: GamePlayGameId,
  ): Promise<CommandResponse<CommandErrorCode>> {
    const settlingGameResponse =
      await this.settlingService.getSettlingGame(gamePlayGameId)

    if (!settlingGameResponse.isSuccessful) {
      return unsuccessfulCommandResponse
    }

    this.settlingGame = settlingGameResponse.result
    return successfulCommandResponse
  }
}
